import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { DIRS, turnDir, rightOf, yawOf } from '../core/directions.js';
import { createRandom } from '../core/random.js';
import { InstancePool } from '../render/InstancePool.js';
import { OBSTACLES, LANES, GEM_VALUE, generateRows, generateItems } from './Generator.js';
import * as M from '../render/Models.js';

const C = CONFIG.path;
const LW = C.laneWidth;
const HW = C.width / 2;
const NORTH = 0;
const EAST = 1;
const SEAM = 0.06; // junta entre losas (y entre carriles): marca los carriles sin líneas aparte

// Tonos de las losas: piedra gris con algunas verdosas (musgo).
const FLOOR_TONES = [0xffffff, 0xf2efe6, 0xdcdcd2, 0xc8d6b8, 0xb5c7a2].map((h) => new THREE.Color(h));
const ROCK_TONES = [0xffffff, 0xe6e9ee, 0xd2d8cf].map((h) => new THREE.Color(h));
const DEAD_TRUNK = new THREE.Color(0x8c8c8c);
const UP = new THREE.Vector3(0, 1, 0);
const ONE = new THREE.Vector3(1, 1, 1);
const _right = new THREE.Vector3();
const _pos = new THREE.Vector3();
const _scale = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _matrix = new THREE.Matrix4();
const _color = new THREE.Color();

/**
 * Matriz de una caja alineada con el tramo:
 * s0..s1 a lo largo, lat0..lat1 a lo ancho (positivo = derecha), y0..y1 en altura.
 * En el espacio local de la instancia, X es la derecha del tramo, Y arriba y -Z hacia delante.
 */
function segmentBox(seg, s0, s1, lat0, lat1, y0, y1, flip = false) {
  rightOf(seg.dir, _right);
  _pos
    .copy(seg.start)
    .addScaledVector(seg.dir, (s0 + s1) / 2)
    .addScaledVector(_right, (lat0 + lat1) / 2);
  _pos.y = (y0 + y1) / 2;
  _quat.setFromAxisAngle(UP, yawOf(seg.dirIndex) + (flip ? Math.PI : 0));
  _scale.set(lat1 - lat0, y1 - y0, s1 - s0);
  return _matrix.compose(_pos, _quat, _scale);
}

/** ¿La pieza cubre el carril? */
/** Matriz de un objeto de tamaño real en (s, lat, y) del tramo, orientado según él. */
function segmentPoint(seg, s, lat, y) {
  rightOf(seg.dir, _right);
  _pos.copy(seg.start).addScaledVector(seg.dir, s).addScaledVector(_right, lat);
  _pos.y = y;
  _quat.setFromAxisAngle(UP, yawOf(seg.dirIndex));
  return _matrix.compose(_pos, _quat, ONE);
}

const PU_GLOW = { magnet: new THREE.Color(0xb04cff), shield: new THREE.Color(0x4cd8ff), sprint: new THREE.Color(0xff8a1c) };

const covers = (piece, lane) => piece.lane <= lane && lane < piece.lane + piece.span;

/**
 * Camino infinito: tramos rectos de 3 carriles unidos por esquinas de 90° y algún cruce en T.
 *
 * Para que el camino nunca se cruce consigo mismo solo se usan tres rumbos: norte, este y oeste.
 * Desde el norte se gira a cualquier lado; desde el este u oeste siempre se vuelve al norte.
 *
 * Cada tramo guarda su origen, dirección, longitud y contenido (obstáculos, fosos, monedas).
 * La posición del jugador se expresa como (tramo, s, x): metros a lo largo y desplazamiento lateral.
 */
export class Path {
  constructor(scene, speedAt) {
    this.speedAt = speedAt;
    this.group = new THREE.Group();
    this.group.name = 'Path';
    scene.add(this.group);

    const mat = (extra) => M.lowPolyMaterial(extra);
    const glow = (hex) => mat({ emissive: hex });
    this.pools = {
      // Capacidades ≈ 2× el uso máximo medido con `npm run simulate`.
      floor: new InstancePool('Floor', M.tileGeometry(), mat(), 700, { colors: true }),
      pillar: new InstancePool('Pillars', M.pillarGeometry(), mat(), 60),
      // Fondo negro de los fosos: sin él, la bruma de abajo se confunde con las losas.
      hole: new InstancePool('PitHoles', new THREE.BoxGeometry(1, 1, 1), new THREE.MeshBasicMaterial({ color: 0x020403 }), 48),
      low: new InstancePool('Obstacle.low', M.logGeometry(), mat(), 32),
      high: new InstancePool('Obstacle.high', M.brambleGeometry(), mat(), 64),
      vine: new InstancePool('Vines', M.vineGeometry(), mat(), 96),
      wall: new InstancePool('Obstacle.wall', M.rockGeometry(), mat(), 120, { colors: true }),
      trunk: new InstancePool('Trunks', M.trunkGeometry(), mat(), 420, { colors: true }),
      canopy: new InstancePool('Canopies', M.canopyGeometry(), mat(), 420, { colors: true }),
      coin: new InstancePool('Coins', M.coinGeometry(), glow(0x4a3200), 180, { dynamic: true }),
      gem: new InstancePool('Gems', M.gemGeometry(), glow(0x5a0818), 24, { dynamic: true }),
      // Potenciadores: formas y colores distintos para reconocerlos de lejos.
      magnet: new InstancePool('PowerUp.magnet', M.magnetGeometry(), glow(0x3a1060), 8, { dynamic: true }),
      shield: new InstancePool('PowerUp.shield', M.shieldGeometry(), glow(0x0d5a6a), 8, { dynamic: true }),
      sprint: new InstancePool('PowerUp.sprint', M.sprintGeometry(), glow(0x6a2a00), 8, { dynamic: true }),
      // Haz de luz y aro bajo cada potenciador: se ven de lejos en la oscuridad.
      beam: new InstancePool('PowerUp.beam', M.beamGeometry(), M.additiveMaterial(0.22), 16, { colors: true }),
      ring: new InstancePool('PowerUp.ring', M.ringGeometry(), M.additiveMaterial(0.7), 16, { colors: true }),
      // Faroles en las esquinas: anuncian la curva.
      lampPost: new InstancePool('LampPosts', M.lampPostGeometry(), mat(), 48),
      lampGlow: new InstancePool('LampGlow', M.lampGlowGeometry(), new THREE.MeshBasicMaterial({ color: 0xffc27a }), 48),
    };
    for (const pool of Object.values(this.pools)) this.group.add(pool.mesh);

    this.live = [];
    this.first = null;
  }

  /** Borra todo y genera un camino nuevo a partir de una semilla. */
  reset(seed) {
    for (const seg of this.live) this.#release(seg);
    this.live = [];
    this.seed = seed;
    this.rand = createRandom(seed);
    this.first = this.#createSegment({
      start: new THREE.Vector3(),
      dirIndex: NORTH,
      length: C.firstLength,
      startDist: 0,
      depth: 0,
      safeStart: C.safeStart,
      isFirst: true,
    });
    this.fill(this.first, 0);
  }

  /** Genera tramos hasta tener C.ahead metros por delante (se detiene en un cruce en T sin decidir). */
  fill(current, s) {
    let seg = current;
    let ahead = seg.length - s;
    while (seg.next) {
      seg = seg.next;
      ahead += seg.length;
    }
    while (ahead < C.ahead && !seg.branches) {
      seg = this.#extend(seg);
      if (!seg) break;
      ahead += seg.length;
    }
  }

  /** El jugador ha elegido rama en un cruce en T. */
  choose(tee, branch) {
    tee.next = branch;
    tee.turn = branch === tee.branches[0] ? -1 : 1;
    for (const b of tee.branches) if (b !== branch) b.abandoned = true;
  }

  /** Libera los tramos que ya quedan atrás (se conserva el anterior, que aún se ve). */
  recycle(current, s) {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const seg = this.live[i];
      const old = seg.depth < current.depth - 1;
      const deadBranch = seg.abandoned && (seg.depth < current.depth || s > 20);
      if (old || deadBranch) {
        this.#release(seg);
        this.live.splice(i, 1);
      }
    }
  }

  /** Recoge una moneda: desaparece de la escena. */
  takeCoin(coin) {
    coin.taken = true;
    this.pools[coin.pool].release(coin.idx);
    coin.idx = -1;
  }

  /** Recoge un potenciador. */
  takePowerUp(pu) {
    pu.taken = true;
    this.pools[pu.type].release(pu.idx);
    pu.idx = -1;
    this.pools.beam.release(pu.beam);
    this.pools.ring.release(pu.ring);
    pu.beam = pu.ring = -1;
  }

  /** Rompe un obstáculo (escudo, sprint o tropiezo con un tronco): deja de verse y de chocar. */
  breakObstacle(o) {
    o.broken = true;
    for (let i = 0; i < o.inst.length; i += 2) this.pools[o.inst[i]].release(o.inst[i + 1]);
    o.inst.length = 0;
  }

  /**
   * Datos de colisión de una pieza de obstáculo (sin crear nada en la escena).
   * s es la posición de la fila; las piezas largas (columna profunda) empiezan en ella.
   */
  obstacleData(s, piece) {
    const t = OBSTACLES[piece.type];
    const depth = piece.length ?? t.depth;
    return {
      s: piece.length ? s + depth / 2 : s, // centro
      type: piece.type,
      xMin: (piece.lane - 0.5) * LW + 0.05,
      xMax: (piece.lane + piece.span - 0.5) * LW - 0.05,
      depth,
      bottom: t.bottom,
      top: t.top,
      inst: [], // [nombreReserva, índice, …] de sus instancias visibles
      hit: false, // ya ha provocado un tropiezo: no vuelve a chocar
      broken: false,
    };
  }

  /** Datos de un foso: tramo sin suelo de s0 a s1 en los carriles de la pieza. */
  pitData(s, piece) {
    return {
      s0: s,
      s1: s + piece.length,
      lane: piece.lane,
      span: piece.span,
      xMin: (piece.lane - 0.5) * LW,
      xMax: (piece.lane + piece.span - 0.5) * LW,
      bridged: false, // cruzado durante el sprint: ya no hace caer
    };
  }

  /** Hace girar las monedas y flotar los potenciadores. */
  animate(time) {
    _quat.setFromAxisAngle(UP, time * 3);
    for (const seg of this.live) {
      for (const c of seg.coins) {
        if (c.taken || c.idx < 0) continue;
        this.pools[c.pool].mesh.setMatrixAt(c.idx, _matrix.compose(c.pos, _quat, ONE));
      }
    }
    this.pools.coin.mesh.instanceMatrix.needsUpdate = true;
    this.pools.gem.mesh.instanceMatrix.needsUpdate = true;

    _quat.setFromAxisAngle(UP, time * 1.5);
    for (const seg of this.live) {
      for (const pu of seg.powerUps) {
        if (pu.taken || pu.idx < 0) continue;
        _pos.copy(pu.pos);
        _pos.y += Math.sin(time * 3 + pu.s) * 0.15;
        this.pools[pu.type].setMatrix(pu.idx, _matrix.compose(_pos, _quat, ONE));
      }
    }
  }

  /** Punto del mundo a s metros del inicio del tramo y x metros a la derecha del eje. */
  worldPoint(seg, s, x, out = new THREE.Vector3()) {
    return out.copy(seg.start).addScaledVector(seg.dir, s).addScaledVector(rightOf(seg.dir, _right), x);
  }

  /** Instancias en uso por reserva, para el panel de depuración y las pruebas. */
  usage() {
    const out = {};
    for (const [k, p] of Object.entries(this.pools)) out[k] = p.used;
    return out;
  }

  // ---------- Generación ----------

  #extend(seg) {
    const r = this.rand;
    const next = {
      start: seg.end,
      startDist: seg.startDist + seg.length,
      depth: seg.depth + 1,
      safeStart: C.safeAfterTurn,
    };
    if (seg.dirIndex === NORTH && seg.depth > 0 && r() < C.teeChance) {
      seg.tee = true;
      seg.branches = [-1, 1].map((t) => {
        const dirIndex = turnDir(seg.dirIndex, t);
        return this.#createSegment({ ...next, dirIndex, length: this.#lengthFor(dirIndex) });
      });
      return null;
    }
    // Desde el norte se gira al azar; desde el este u oeste, siempre de vuelta al norte.
    const turn = seg.dirIndex === NORTH ? (r() < 0.5 ? -1 : 1) : seg.dirIndex === EAST ? -1 : 1;
    const dirIndex = turnDir(seg.dirIndex, turn);
    seg.turn = turn;
    seg.next = this.#createSegment({ ...next, dirIndex, length: this.#lengthFor(dirIndex) });
    return seg.next;
  }

  #lengthFor(dirIndex) {
    return dirIndex === NORTH ? this.rand.range(35, 70) : this.rand.range(22, 45);
  }

  #createSegment({ start, dirIndex, length, startDist, depth, safeStart, isFirst = false }) {
    const seg = {
      depth,
      start: start.clone(),
      dirIndex,
      dir: DIRS[dirIndex],
      length,
      end: start.clone().addScaledVector(DIRS[dirIndex], length),
      startDist,
      turn: 0, // -1 / +1, se decide al generar el tramo siguiente
      tee: false,
      next: null,
      branches: null,
      abandoned: false,
      rows: [],
      obstacles: [],
      pits: [],
      coins: [],
      powerUps: [],
      inst: [], // [nombreReserva, índice, …] para liberar al reciclar
    };

    seg.rows = generateRows(this.rand, {
      length,
      startDist,
      safeStart,
      endMargin: C.endMargin,
      speedAt: this.speedAt,
    });
    for (const row of seg.rows) {
      for (const piece of row.pieces) {
        if (piece.type === 'pit') seg.pits.push(this.pitData(row.s, piece));
        else this.#addObstacle(seg, row.s, piece);
      }
    }

    for (const pit of seg.pits) {
      this.#add(seg, 'hole', segmentBox(seg, pit.s0 + 0.03, pit.s1 - 0.03, pit.xMin + 0.03, pit.xMax - 0.03, -C.thickness + 0.02, -0.12));
    }
    // Suelo: cubre también el cuadrado de la esquina final; el siguiente tramo empieza tras él.
    this.#addFloor(seg, isFirst ? -C.startPad : HW, length + HW);

    const items = generateItems(this.rand, seg.rows, { length, startDist, powerUps: CONFIG.powerUps });
    for (const c of items.coins) {
      const x = c.lane * LW;
      const pos = this.worldPoint(seg, c.s, x);
      pos.y = c.y;
      const pool = c.gem ? 'gem' : 'coin';
      const idx = this.pools[pool].alloc(_matrix.makeTranslation(pos));
      seg.coins.push({ s: c.s, x, y: c.y, pos, idx, pool, value: c.gem ? GEM_VALUE : 1, taken: false, flying: false });
    }
    for (const p of items.powerUps) {
      const x = p.lane * LW;
      const pos = this.worldPoint(seg, p.s, x);
      pos.y = p.y;
      const idx = this.pools[p.type].alloc(_matrix.makeTranslation(pos));
      const beam = this.pools.beam.alloc(segmentPoint(seg, p.s, x, 0), PU_GLOW[p.type]);
      const ring = this.pools.ring.alloc(segmentPoint(seg, p.s, x, 0.03), PU_GLOW[p.type]);
      seg.powerUps.push({ s: p.s, x, y: p.y, type: p.type, pos, idx, beam, ring, taken: false });
    }

    // Faroles a ambos lados del fondo de la esquina (también en los cruces en T).
    for (const side of [-1, 1]) {
      const s = length + HW + 0.35;
      const lat = side * (HW + 0.35);
      this.#add(seg, 'lampPost', segmentPoint(seg, s, lat, 0));
      this.#add(seg, 'lampGlow', segmentPoint(seg, s, lat, 0));
    }

    this.#addTrees(seg, isFirst);
    this.live.push(seg);
    return seg;
  }

  /** Losas de piedra por carril, de longitud irregular; donde hay un foso no se pone ninguna. */
  #addFloor(seg, s0, s1) {
    const r = this.rand;
    for (const lane of LANES) {
      const lat0 = (lane - 0.5) * LW;
      const lat1 = lat0 + LW;
      const holes = seg.pits.filter((p) => covers(p, lane)).sort((a, b) => a.s0 - b.s0);
      let h = 0;
      let s = s0;
      while (s < s1 - 0.05) {
        while (h < holes.length && holes[h].s1 <= s) h++;
        const hole = holes[h];
        if (hole && hole.s0 <= s) {
          s = hole.s1;
          continue;
        }
        let e = Math.min(s1, s + r.range(1.5, 2.6));
        if (s1 - e < 0.6) e = s1; // sin losas diminutas al final
        if (hole && hole.s0 < e) e = hole.s0;
        _color.copy(FLOOR_TONES[Math.floor(r() * FLOOR_TONES.length)]).multiplyScalar(r.range(0.85, 1.05));
        const top = r.range(-0.02, 0.02);
        this.#add(seg, 'floor', segmentBox(seg, s + SEAM / 2, e - SEAM / 2, lat0 + SEAM / 2, lat1 - SEAM / 2, -C.thickness, top), _color);
        s = e;
      }
    }
  }

  #addObstacle(seg, s, piece) {
    const o = this.obstacleData(s, piece);
    const { xMin, xMax } = o;
    const s0 = o.s - o.depth / 2;
    const s1 = o.s + o.depth / 2;
    // Las instancias van en el propio obstáculo (no en seg.inst) porque puede romperse antes de reciclar el tramo.
    const add = (pool, matrix, color) => {
      const idx = this.pools[pool].alloc(matrix, color);
      if (idx >= 0) o.inst.push(pool, idx);
    };
    const r = this.rand;
    const rockTone = () => _color.copy(ROCK_TONES[Math.floor(r() * ROCK_TONES.length)]).multiplyScalar(r.range(0.85, 1.05));
    switch (piece.type) {
      case 'deep':
        // Columna profunda: rocas encadenadas (y algo solapadas) a lo largo de todo el obstáculo.
        for (let a = s0; a < s1 - 0.1; ) {
          const b = Math.min(s1, a + r.range(1.8, 2.8));
          const box = segmentBox(seg, a - 0.25, b + 0.25, xMin - 0.04, xMax + 0.04, 0, o.top + r.range(-0.35, 0.25), r() < 0.5);
          add('wall', box, rockTone());
          a = b;
        }
        break;
      case 'wall':
        add('wall', segmentBox(seg, s0 - 0.1, s1 + 0.1, xMin, xMax, 0, o.top, r() < 0.5), rockTone());
        break;
      case 'high': {
        // Zarza: una pieza por carril, colgada de lianas que suben hasta las copas.
        for (let l = piece.lane; l < piece.lane + piece.span; l++) {
          const lat0 = Math.max(xMin, (l - 0.5) * LW);
          const lat1 = Math.min(xMax, (l + 0.5) * LW);
          add('high', segmentBox(seg, s0, s1, lat0, lat1, o.bottom - 0.05, o.top + 0.05, r() < 0.5));
        }
        const vines = [xMin + 0.12, xMax - 0.12];
        if (piece.span === 3) vines.push(0);
        for (const v of vines) add('vine', segmentBox(seg, o.s - 0.05, o.s + 0.05, v - 0.05, v + 0.05, o.top - 0.3, o.top + 11));
        break;
      }
      default:
        add(piece.type, segmentBox(seg, s0, s1, xMin, xMax, o.bottom, o.top));
    }
    seg.obstacles.push(o);
  }

  /**
   * Bosque a los lados: una hilera cercana (da sensación de velocidad) y otra lejana más densa.
   * Las copas nunca invaden el camino de este tramo ni el de los tramos vecinos en las esquinas.
   * Bajo el camino, pilares de piedra que se pierden en la niebla.
   */
  #addTrees(seg, isFirst) {
    const r = this.rand;
    const sMin = isFirst ? -C.startPad : HW + 1;
    const sMax = seg.length - HW - 1;
    // Distancia libre hasta el camino propio y hasta los tramos de las esquinas.
    const clearance = (s, lat) => Math.min(Math.abs(lat) - HW, isFirst ? Infinity : s - HW, seg.length - HW - s);
    for (let s = sMin; s < sMax; s += r.range(2.2, 4.2)) {
      const lat = (r() < 0.5 ? -1 : 1) * (HW + r.range(1.3, 6));
      this.#addTree(seg, s, lat, clearance(s, lat));
    }
    for (let s = sMin; s < sMax; s += r.range(3, 5.5)) {
      for (const side of [-1, 1]) {
        if (r() < 0.3) continue;
        const lat = side * (HW + r.range(7, 24));
        this.#addTree(seg, s, lat, clearance(s, lat));
      }
    }
    for (let s = isFirst ? 0 : HW; s < seg.length; s += r.range(18, 30)) {
      this.#add(seg, 'pillar', segmentBox(seg, s - 1, s + 1, -1.1, 1.1, -45, -C.thickness + 0.05));
    }
    this.#add(seg, 'pillar', segmentBox(seg, seg.length - 1.2, seg.length + 1.2, -1.3, 1.3, -45, -C.thickness + 0.05));
  }

  #addTree(seg, s, lat, clearance) {
    const r = this.rand;
    if (clearance < 0.8) return;
    // Cerca de la esquina la cámara barre por fuera del camino al girar: ahí solo árboles lejanos.
    if (seg.length - s < 11 && Math.abs(lat) < HW + 9) return;
    const far = clearance > 6;
    const trunkR = r.range(0.25, 0.45) * (far ? 1.7 : 1);
    if (r() < 0.12) {
      // Árbol muerto: tocón pelado y claro, bajo para que no parezca un poste.
      _color.copy(DEAD_TRUNK).multiplyScalar(r.range(0.7, 1.1));
      const top = r.range(1.5, 4.5);
      this.#add(seg, 'trunk', segmentBox(seg, s - trunkR, s + trunkR, lat - trunkR, lat + trunkR, -40, top), _color);
      return;
    }
    const radius = Math.min(r.range(1.3, 2.4) * (far ? 1.5 : 1), clearance - 0.3);
    if (radius < 0.6) return;
    const base = r.range(-1.5, 2.5);
    const height = radius * r.range(2.4, 3.6);
    _color.setScalar(r.range(0.75, 1.1));
    this.#add(seg, 'trunk', segmentBox(seg, s - trunkR, s + trunkR, lat - trunkR, lat + trunkR, -40, base + 0.5), _color);
    _color.setRGB(r.range(0.8, 1.05), r.range(0.85, 1.1), r.range(0.8, 1.05));
    this.#add(seg, 'canopy', segmentBox(seg, s - radius, s + radius, lat - radius, lat + radius, base, base + height), _color);
  }

  #add(seg, poolName, matrix, color) {
    const idx = this.pools[poolName].alloc(matrix, color);
    if (idx >= 0) seg.inst.push(poolName, idx);
  }

  #release(seg) {
    for (let i = 0; i < seg.inst.length; i += 2) this.pools[seg.inst[i]].release(seg.inst[i + 1]);
    seg.inst.length = 0;
    for (const o of seg.obstacles) if (!o.broken) this.breakObstacle(o);
    // Se marcan como recogidos para que nadie (p. ej. el imán) vuelva a liberarlos.
    for (const c of seg.coins) if (!c.taken) this.takeCoin(c);
    for (const p of seg.powerUps) if (!p.taken) this.takePowerUp(p);
    seg.coins.length = 0;
    seg.powerUps.length = 0;
  }
}
